/**
 * The field width scale — measure, not layout.
 *
 * docs/018-ux-standards.md, "Measure": a field's width is a message about the
 * content it expects. A 5-character postcode rendered 900px wide tells the user
 * to expect a paragraph. Pick the SMALLEST token the longest realistic value
 * fits in; when unsure, `md`.
 *
 * Every entry is a `max-width`, never a fixed `width`: the field still shrinks
 * inside a narrow column or on a phone, so the scale costs nothing responsive.
 *
 * `md` and `lg` are capped only from `sm` up. Below that the column is about 345px on
 * a common phone, so a 288px cap leaves a ~57px gap that reads as a mistake rather than
 * a decision, and the 416px cap never binds at all. `xs` and `sm` stay capped at every
 * width: a 96px or 160px box is a small fraction of a phone column, so it still reads as
 * deliberate. Verified against a 393px capture, not reasoned about —
 * `UX_SHOTS_VIEWPORT=mobile pnpm ux:shots`.
 *
 * WHY A LOOKUP TABLE AND NOT `\`max-w-${n}\``: Tailwind scans source text for
 * complete class names, so a template literal produces a class that exists at
 * runtime and was never generated at build time (the same trap documented at
 * length in `chart-series.ts`). `styles/index.css` declares `../lib` as a
 * `@source` for exactly this reason — every class name below is written out.
 */
/**
 * Token → Tailwind class. `full` maps to the empty string rather than
 * `max-w-full`: "no constraint" must add nothing, so that a caller's own
 * `className` (a dialog's `w-full`, a grid child's `min-w-0`) is not competing
 * with a utility this package stamped on unasked.
 *
 * | Token | Max width | Content it holds                                  |
 * |-------|-----------|---------------------------------------------------|
 * | `xs`  | 96px      | PIN, 2–4 digit number, quantity, floor, port       |
 * | `sm`  | 160px     | Phone, date, time, currency, postcode, short code  |
 * | `md`  | 288px     | First/last name, city, short label, most selects   |
 * | `lg`  | 416px     | Email, company, URL, street address, long name     |
 * | `full`| —         | Textarea, rich text, search, any field in a dialog |
 */
export const FIELD_WIDTH_CLASSES = {
    xs: "max-w-24",
    sm: "max-w-40",
    md: "sm:max-w-72",
    lg: "sm:max-w-104",
    full: "",
};
/**
 * The class for a width token, or for `undefined`.
 *
 * `undefined` resolves to `full` — no constraint, which is what every field in
 * the product renders as today. The default is deliberately NOT `md`: making
 * `md` the default would silently narrow every existing field the moment this
 * ships. Choosing a width is a per-field editorial decision, made in the
 * retrofit; `ux:check` flags the fields that never chose.
 */
export function fieldWidthClass(width = "full") {
    return FIELD_WIDTH_CLASSES[width];
}
//# sourceMappingURL=field-width.js.map